import React, { useCallback, useEffect, useMemo, useState } from 'react'

// prettier-ignore
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'shadcdn/ui/alert-dialog'
import type { VersionState, VersionStatus } from 'features/versions/types'
import { useUpdateAvailable, useVersionQuery, useVersionStatus } from 'features/software/hooks'
import { useInstalledComponents } from 'features/app/hooks'
import { useAuthStore } from 'features/auth/useAuthStore'
import { useShowAuthModal } from 'features/auth/AuthModalOverlay'
import { getDefaultVersionState } from 'features/software/constants'
import { RosettaRequiredError } from 'features/software/SoftwareVersionBlock/RosettaRequiredError'
import { trackPresyncEvent } from 'features/analytics/analytics'
import { useShowAppRequiredOverlay } from 'features/app/AppRequiredOverlay'
import { isRunningInElectron } from 'utils/electron'
import { appAPI } from 'utils/app-api'

import { VersionActionButton } from './VersionActionButton'
import { VersionTypeBadge } from './VersionTypeBadge'

type Props = {
  versionState?: VersionState
  versionId: number
  softwareSlug: string // used in event tracking
}

export const SoftwareVersionBlock = React.memo((props: Props) => {
  const { versionId, softwareSlug } = props

  // prettier-ignore
  const {
    version,
    versionState,
    installVersion,
    errorMessage,
    clearErrorMessage,
    uninstallVersion,
    cancelInstallation,
  } = useSoftwareVersionBlockData(softwareSlug, versionId)

  if (!version) return null

  const versionComponents = Array.from(
    new Set(version.components.map((filename) => filename.split('.').pop()) as string[]),
  )

  return (
    <div className="flex flex-col gap-y-2 rounded">
      <VersionActionButton
        installVersion={installVersion}
        versionState={versionState}
        uninstallVersion={uninstallVersion}
        cancelInstallation={cancelInstallation}
      />

      <div className="text-gray-500  text-xs text-center flex flex-col gap-y-1">
        <div>{versionComponents.join(', ')}</div>
        <div>
          {version.version && <span>Version: {version.version}</span>}{' '}
          {version.architecture && version.architecture !== 'universal' && (
            <span className="text-gray-400">({version.architecture === 'arm' ? 'Apple Silicon' : 'Intel'})</span>
          )}{' '}
          <VersionTypeBadge type={version.type} />
        </div>
      </div>

      <ErrorModal errorMessage={errorMessage} onClose={clearErrorMessage} />
    </div>
  )
})

function ErrorModal(props: { errorMessage: React.ReactNode; onClose: () => void }) {
  return (
    <AlertDialog open={!!props.errorMessage}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Installation error</AlertDialogTitle>
          <AlertDialogDescription>{props.errorMessage}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={props.onClose}>Okay</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/************************************************************************************************************** **/

export function useSoftwareVersionBlockData(softwareSlug: string, versionId: number) {
  const { version } = useVersionQuery(versionId)
  const { refetch: updateInstalledComponents } = useInstalledComponents()

  const { user } = useAuthStore()
  const showAuthForm = useShowAuthModal()

  /** Error handling */
  const [errorMessage, setErrorMessage] = useState<string | React.ReactNode>('')
  const clearErrorMessage = () => {
    setErrorMessage(null)
    setVersionState(defaultVersionState)
  }

  /** Version state */
  let status: VersionStatus = useVersionStatus(version) ?? 'notInstalled'
  const updateAvailable = useUpdateAvailable(version)
  if (updateAvailable) status = 'updateAvailable'

  const defaultVersionState = useMemo<VersionState>(() => getDefaultVersionState(status), [status])
  const [versionState, setVersionState] = useState<VersionState>(defaultVersionState)
  useEffect(() => {
    if (!defaultVersionState) return
    setVersionState(defaultVersionState)
  }, [defaultVersionState])

  const onVersionStateUpdate = useCallback(
    (versionState: VersionState) => {
      if (versionState.status === 'error' && versionState.message!.includes('Rosetta 2')) {
        setErrorMessage(<RosettaRequiredError />)
        const errorMessage = versionState.message
        trackPresyncEvent(trackPresyncEvent.events.SOFTWARE_INSTALL_ERROR, { softwareSlug, versionId, errorMessage })
      }

      if (versionState.status === 'done') {
        trackPresyncEvent(trackPresyncEvent.events.SOFTWARE_INSTALL_DONE, { softwareSlug, versionId })
        return updateInstalledComponents()
      }
      setVersionState(versionState)
    },
    [softwareSlug, updateInstalledComponents, versionId],
  )

  const showAppRequiredOverlay = useShowAppRequiredOverlay()

  const uninstallVersion = useCallback(async () => {
    if (!version) return
    try {
      await appAPI.deleteComponents(version.components)
    } catch (e) {
      console.error(e)
    }

    await updateInstalledComponents()
  }, [updateInstalledComponents, version])

  const installVersion = useCallback(async () => {
    if (!user) return showAuthForm('Please sign up to install software', 'signup')

    setErrorMessage(null)
    if (!isRunningInElectron) {
      const installedSoftwareSlug = softwareSlug ?? ''
      trackPresyncEvent(trackPresyncEvent.events.APP_REQUIRED_OVERLAY_SHOWN, { props: { installedSoftwareSlug } })
      return showAppRequiredOverlay()
    }
    trackPresyncEvent(trackPresyncEvent.events.SOFTWARE_INSTALL_CLICK, { softwareSlug })
    setVersionState({ status: 'downloading', progress: 0 })
    appAPI.startInstall(versionId.toString())
  }, [user, softwareSlug, versionId, showAuthForm, showAppRequiredOverlay])

  /**
   * Cancels ongoing software installation process
   * Sends cancel signal to Electron main process and resets version state
   */
  const cancelInstallation = useCallback(() => {
    if (!isRunningInElectron) return

    appAPI.cancelInstall(versionId.toString())
    setVersionState(defaultVersionState)
    trackPresyncEvent(trackPresyncEvent.events.SOFTWARE_INSTALL_CANCEL, { softwareSlug, versionId })
  }, [versionId, defaultVersionState, softwareSlug])

  useEffect(() => {
    if (!isRunningInElectron) return
    const removeListener = appAPI.onVersionStateUpdate(versionId.toString(), onVersionStateUpdate)
    return () => {
      removeListener?.()
    }
  }, [onVersionStateUpdate, versionId])

  return {
    version,
    defaultVersionState,
    versionState,
    setVersionState,
    installVersion,
    uninstallVersion,
    cancelInstallation,
    clearErrorMessage,
    errorMessage,
  }
}
